import React, { useState, useRef, useEffect } from "react";

// Deterministic avatar colors for the initials fallback (Jira-style)
const AVATAR_COLORS = ["#2563eb", "#7c3aed", "#0d9488", "#ea580c", "#db2777", "#4f46e5", "#059669", "#b45309"];

const colorForName = (name) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = (hash * 31 + name.charCodeAt(i)) | 0;
    }
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const initialsForName = (name) =>
    name
        .split(" ")
        .filter(Boolean)
        .map((word) => word[0])
        .slice(0, 2)
        .join("")
        .toLocaleUpperCase("tr");

export const TeacherAvatar = ({ teacher, large }) => {
    const sizeClass = large ? " olp-avatar--lg" : "";
    if (teacher && teacher.ImageUrl) {
        return <img className={"olp-avatar" + sizeClass} src={teacher.ImageUrl} alt={teacher.TeacherName} />;
    }
    const name = teacher ? teacher.TeacherName : "?";
    return (
        <span
            className={"olp-avatar olp-avatar--initials" + sizeClass}
            style={{ backgroundColor: colorForName(name) }}
        >
            {initialsForName(name)}
        </span>
    );
};

// Jira-style assignee picker: avatar + name rows, search box, keyboard support.
// `teachers` must already be sorted (useLectureData sorts Id-descending);
// `extraOptions` (e.g. "Pakodemy Hoca", Id 0) are appended after the list.
// Emits the same value the legacy <select> produced: the teacher Id.
const TeacherSelect = ({ teachers, extraOptions = [], value, onChange, placeholder = "Öğretmen Seçiniz" }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [activeIndex, setActiveIndex] = useState(0);
    const rootRef = useRef(null);
    const searchRef = useRef(null);

    const allOptions = [...teachers, ...extraOptions];
    const selected = allOptions.find((t) => String(t.Id) === String(value)) || null;

    const filtered = query
        ? allOptions.filter((t) =>
              t.TeacherName.toLocaleLowerCase("tr").includes(query.toLocaleLowerCase("tr"))
          )
        : allOptions;

    useEffect(() => {
        if (!open) return;
        const onDocMouseDown = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) {
                setOpen(false);
                setQuery("");
            }
        };
        document.addEventListener("mousedown", onDocMouseDown);
        return () => document.removeEventListener("mousedown", onDocMouseDown);
    }, [open]);

    useEffect(() => {
        if (open && searchRef.current) {
            searchRef.current.focus();
        }
        setActiveIndex(0);
    }, [open, query]);

    const pick = (teacher) => {
        onChange(teacher.Id);
        setOpen(false);
        setQuery("");
    };

    const onKeyDown = (e) => {
        if (e.key === "Escape") {
            setOpen(false);
            setQuery("");
        } else if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex((i) => Math.max(i - 1, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (filtered[activeIndex]) pick(filtered[activeIndex]);
        }
    };

    return (
        <div className={"olp-teacher-select" + (open ? " olp-teacher-select--open" : "")} ref={rootRef}>
            <div
                className="olp-teacher-select-control"
                tabIndex={0}
                role="button"
                onClick={() => setOpen(!open)}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setOpen(!open);
                    }
                }}
            >
                {selected ? (
                    <React.Fragment>
                        <TeacherAvatar teacher={selected} />
                        <span>{selected.TeacherName}</span>
                    </React.Fragment>
                ) : (
                    <span className="olp-teacher-select-placeholder">{placeholder}</span>
                )}
                <span className="olp-teacher-select-caret">▾</span>
            </div>
            {open && (
                <div className="olp-teacher-select-menu" onKeyDown={onKeyDown}>
                    <input
                        ref={searchRef}
                        className="olp-teacher-select-search"
                        placeholder="Öğretmen ara..."
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                    <ul className="olp-teacher-select-list">
                        {filtered.length === 0 && <li className="olp-teacher-select-empty">Öğretmen bulunamadı</li>}
                        {filtered.map((teacher, index) => (
                            <li
                                key={teacher.Id}
                                className={
                                    "olp-teacher-option" +
                                    (index === activeIndex ? " olp-teacher-option--active" : "") +
                                    (selected && selected.Id === teacher.Id ? " olp-teacher-option--selected" : "")
                                }
                                onMouseEnter={() => setActiveIndex(index)}
                                onClick={() => pick(teacher)}
                            >
                                <TeacherAvatar teacher={teacher} />
                                <span className="olp-teacher-option-name">{teacher.TeacherName}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default TeacherSelect;
